// src/components/Now.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../shared/components/layout/Layout';
import { Card, Alert, Button } from '../../../shared/components/ui';
import MealDetailView from '../components/MealDetailView';
import EditRecipeModal from '../components/EditRecipeModal';
import GenerateRecipeModal from '../components/GenerateRecipeModal';
import { mealService } from '../services/mealService';
import { recipeService } from '../services/recipeService';
import { useRecipeSave } from '../hooks/useRecipeSave';
import { useTranslation } from 'react-i18next';

const Now = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { saveRecipe } = useRecipeSave();
    const [currentMeal, setCurrentMeal] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentTime, setCurrentTime] = useState(new Date());
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedRecipe, setSelectedRecipe] = useState(null);
    const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
    const [generatedRecipe, setGeneratedRecipe] = useState(null);
    const [isCreateMode, setIsCreateMode] = useState(false);
    const [recipeCreationStatus, setRecipeCreationStatus] = useState(null);
    const [isLoadingStatus, setIsLoadingStatus] = useState(false);

    // Actualizar la hora cada minuto
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000); // Cada minuto

        return () => clearInterval(interval);
    }, []);

    // Cargar el meal actual
    useEffect(() => {
        loadCurrentMeal();
    }, []);

    // Cargar estado de recetas cuando no hay menú disponible
    useEffect(() => {
        if (!isLoading && !currentMeal && !error) {
            loadRecipeCreationStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, currentMeal, error]);

    const loadCurrentMeal = async () => {
        setIsLoading(true);
        setError('');

        try {
            const response = await mealService.getCurrentMeal();
            setCurrentMeal(response.meal);
        } catch (err) {
            setError(t('menu.errors.loadFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    const loadRecipeCreationStatus = async () => {
        setIsLoadingStatus(true);
        try {
            const result = await recipeService.getRecipeCreationStatus();
            if (result.success) {
                setRecipeCreationStatus(result.data);
            }
        } catch (err) {
            // Error loading recipe creation status - no need to display to user
        } finally {
            setIsLoadingStatus(false);
        }
    };

    const handleOpenGenerateModal = () => {
        setIsGenerateModalOpen(true);
    };

    const handleRecipeGenerated = (recipe) => {
        setGeneratedRecipe(recipe);
        setIsCreateMode(true);
        setIsGenerateModalOpen(false);
        setIsEditModalOpen(true);
    };

    const handleSaveRecipe = async (data) => {
        const recipeId = isCreateMode ? null : selectedRecipe?.id;

        await saveRecipe(data, recipeId, async (savedRecipe) => {
            setIsEditModalOpen(false);
            setSelectedRecipe(null);
            setGeneratedRecipe(null);
            setIsCreateMode(false);

            // Si es modo creación, redirigir a la receta creada
            if (isCreateMode && savedRecipe?.id) {
                navigate(`/recipe/${savedRecipe.id}`);
            } else {
                // Recargar el estado de creación solo si no se redirige
                await loadRecipeCreationStatus();
            }
        });
    };

    const getCurrentMealType = () => {
        const hour = currentTime.getHours();
        if (hour >= 6 && hour < 12) return 'breakfast';
        if (hour >= 12 && hour < 18) return 'lunch';
        return 'dinner';
    };

    const getMealTypeEmoji = (mealType) => {
        switch (mealType) {
            case 'breakfast': return '🌅';
            case 'lunch': return '☀️';
            case 'dinner': return '🌙';
            default: return '🍽️';
        }
    };

    const getMealTypeName = (mealType) => {
        switch (mealType) {
            case 'breakfast': return t('meal.types.breakfast');
            case 'lunch': return t('meal.types.lunch');
            case 'dinner': return t('meal.types.dinner');
            default: return t('navigation.menu');
        }
    };

    const formatTime = (date) => {
        return date.toLocaleTimeString('es-ES', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDate = (date) => {
        return date.toLocaleDateString('es-ES', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (isLoading) {
        return (
            <Layout title={`${t('plan.now.title') || t('navigation.now')} 🕐`}>
                <div className="max-w-4xl mx-auto p-6">
                    <Alert variant="info" className="text-center">
                        <p>{t('common.loading')}</p>
                    </Alert>
                </div>
            </Layout>
        );
    }

    return (
        <Layout title={`${t('plan.now.title') || t('navigation.now')} 🕐`}>
            <div className="max-w-4xl mx-auto p-6">
                {/* Información de tiempo actual */}
                <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">
                            {formatTime(currentTime)}
                        </h2>
                        <p className="text-lg text-gray-600 mb-1">
                            {formatDate(currentTime)}
                        </p>
                        <p className="text-sm text-gray-500">
                            {t('now.timeFor') || 'Es hora de:'} <span className="font-semibold text-indigo-600">
                                {getMealTypeEmoji(getCurrentMealType())} {getMealTypeName(getCurrentMealType())}
                            </span>
                        </p>
                    </div>
                </Card>

                {error && (
                    <Alert variant="error" className="mb-6">
                        <p>{error}</p>
                        <button
                            onClick={loadCurrentMeal}
                            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                        >
                            {t('common.retry') || 'Reintentar'}
                        </button>
                    </Alert>
                )}

                {!currentMeal && !error && (
                    <>
                        <Alert variant="info" className="mb-6">
                            <p>
                                {t('now.menuGenerationExplanation') || 'Debes tener en cuenta que cuando los menús se crean para la semana siguiente, para que tengas tiempo a revisar el menú y crear la lista de la compra, por ello hasta la semana que viene aquí no te aparecerá nada, mientras tanto puedes crear recetas a tu gusto y seguir los pasos fácilmente.'}
                            </p>
                        </Alert>
                        <Card className="mb-6">
                            <div className="text-center py-6">
                                <div className="text-6xl mb-4">🍽️</div>
                                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                                    {t('now.noMenu') || 'No hay menú disponible para este momento.'}
                                </h3>
                                <p className="text-gray-600 mb-4">
                                    {t('now.noMenuWithRecipes') || 'Ya tienes recetas en tu plan o se están generando.'}
                                </p>

                            {isLoadingStatus ? (
                                <p className="text-gray-500">{t('common.loading')}</p>
                            ) : recipeCreationStatus && (
                                <>
                                    {recipeCreationStatus.can_create_more ? (
                                        <>
                                            {recipeCreationStatus.recipes_created_last_7_days > 0 && (
                                                <p className="text-sm text-gray-600 mb-4">
                                                    {t('now.recipesRemaining', { remaining: recipeCreationStatus.remaining })}
                                                </p>
                                            )}
                                            <Button
                                                variant="primary"
                                                onClick={handleOpenGenerateModal}
                                                className="mt-2"
                                            >
                                                {t('now.createRecipeButton') || t('menu.edit.titleNew')}
                                            </Button>
                                        </>
                                    ) : (
                                        <p className="text-sm text-orange-600 font-medium">
                                            {t('now.recipeLimit', { limit: recipeCreationStatus.limit })}
                                        </p>
                                    )}
                                </>
                            )}
                            </div>
                        </Card>
                    </>
                )}

                {currentMeal && (
                    <div className="space-y-6">
                        <MealDetailView meal={currentMeal} />
                    </div>
                )}

                {/* Modal de generación de receta con IA */}
                <GenerateRecipeModal
                    isOpen={isGenerateModalOpen}
                    onClose={() => setIsGenerateModalOpen(false)}
                    onRecipeGenerated={handleRecipeGenerated}
                />

                {/* Modal de edición/creación de receta */}
                <EditRecipeModal
                    isOpen={isEditModalOpen}
                    onClose={() => {
                        setIsEditModalOpen(false);
                        setSelectedRecipe(null);
                        setGeneratedRecipe(null);
                        setIsCreateMode(false);
                    }}
                    recipe={selectedRecipe}
                    initialRecipe={generatedRecipe}
                    isCreateMode={isCreateMode}
                    onSave={handleSaveRecipe}
                />
            </div>
        </Layout>
    );
};

export default Now;
